import { RouterProvider } from "react-router-dom";
import { createBrowserRouter } from "react-router-dom";
import { Suspense } from "react";
import { routerRoot, routerItems } from "@/app/router/index";
import { RouterItemType } from "../types/index";
import { useContextKeycloak } from "@/app/module/keycloak/index";
import { LocalizationAvailableLangSchema } from "@/schemas/index";
import { useContextLocalization } from "@/app/module/localization/index";
import { useMemo } from "react";
import { Spin } from "@/base/components/index";

const partLang = ":lang?";

export const ContextRouterProvider = () => {
  const { authorize } = useContextKeycloak();
  const { currentLang } = useContextLocalization();

  const defaultLoader = async (args: any, router: any) => {
    const { lang } = args.params;

    if (router?.authorize && !authorize) {
      throw new Error("ACCESS_DENIED");
    }

    if (router?.localization === false) {
      return router?.loader?.(args);
    }

    if (!lang) {
      throw new Error("LANG_NOT_SET");
    }

    if (!LocalizationAvailableLangSchema.safeParse(lang).success) {
      throw new Error("LANG_NOT_AVAILABLE");
    }

    if (
      lang !== currentLang &&
      LocalizationAvailableLangSchema.safeParse(lang).success
    ) {
      throw new Error("LANG_NOT_UPDATE");
    }

    return router?.loader?.(args);
  };

  const browserRouter = useMemo(() => {
    routerRoot.children = [
      ...(routerRoot?.children || []),
      ...(routerItems || []).map((router: RouterItemType) => {
        const path = `${router.localization !== false ? partLang : ""}/${
          router.path?.[0] && router.path?.[0] === "/"
            ? router.path.slice(1)
            : router.path
        }`;
        return {
          ...router,
          path,
          loader: async (args: any) =>
            await defaultLoader(args, { ...router, path }),
        };
      }),
    ];

    return createBrowserRouter([routerRoot]);
  }, []);

  return (
    <Suspense fallback={<Spin fullscreen size="large" />}>
      <RouterProvider router={browserRouter} />
    </Suspense>
  );
};
